import type { TextEngine } from '@meitu/x-design-text';
import { version as engineVersion } from '@meitu/x-design-text/package.json';
import type { IService } from '../../shared';
import type { ServiceManager } from '../service-manager';
import { TextEngineServiceStatus } from './types';

const DEFAULT_WASM_PATH = '/wasm/x-design-text_bg.wasm';

export class TextEngineService implements IService {
    /**
     *  wasm 资源地址；使用 `PUBLIC_URL` 拼接默认路径
     */
    declare private wasm: string;

    /**
     * 当前已创建的文本引擎实例
     *
     * ATTENTION: 仅在 `READY` 且未被 dispose 时可复用
     */
    private engine: TextEngine | null = null;

    /**
     * 当前 Engine 字体状态的递增版本，不表示 Engine 实例数量。
     *
     * 即使 Engine 实例不变，`registerFont`、`setCommonFallback` 和 `setScriptFallback` 仍会改变
     * 它内部的字体集合与字体选择规则。外部消费者可以保存读取数据时的版本，并在之后比较版本是否
     * 相同，从而判断这些派生数据是否仍基于当前字体状态。
     *
     * 尤其是长期缓存并复用的 TextBuffer：它可能仍持有旧字体状态下生成的塑形和排版结果。
     * 缓存方应同时记录该 Buffer 对应的版本；版本变化后释放旧 Buffer，并通过当前 Engine 重新创建，
     * 不能只因为 Engine 对象没有变化就继续复用。
     *
     * 此版本只表示“字体状态是否变化过”，不记录具体修改了哪个字体；数值本身没有业务含义。
     */
    private fontRevision = 0;

    /**
     * 服务生命周期状态，用于区分空闲、初始化中、可用、异常和已销毁
     */
    private status: TextEngineServiceStatus = TextEngineServiceStatus.Idle;

    constructor(readonly service: ServiceManager) {}

    onInit() {
        if (this.status === TextEngineServiceStatus.Disposed) {
            this.status = TextEngineServiceStatus.Idle;
        }
    }

    // ATTENTION: wasm 完整路径应该在 onCreate 生命周期中初始化
    // 如果在 constructor 中初始化，可能会因为 config 还没有初始化导致没有拿到正确的 PUBLIC_URL
    // 如果在 onInit 生命周期中初始化，还要取决于业务侧是否有调用 service.init() 方法，才能保证 config 已经初始化
    onCreate() {
        const publicURL = (this.service.getConfig().PUBLIC_URL ?? '').replace(/\/$/, ''); // 去掉末尾的斜杠，防止拼接出错
        this.wasm = `${publicURL}${DEFAULT_WASM_PATH}?v=${encodeURIComponent(engineVersion)}`;
    }

    onUnmount() {
        this.dispose();
    }

    /**
     * 显式启动文本引擎。
     *
     * 业务必须先调用该方法完成初始化，字体子集/fallback 流程只通过 `getEngine` 消费启动结果。
     */
    public async initialize(): Promise<void> {
        if (this.status !== TextEngineServiceStatus.Idle) return;

        this.status = TextEngineServiceStatus.Initializing;

        try {
            const sdk = await import('@meitu/x-design-text');
            const { initialize: init, TextEngine } = sdk;

            try {
                // 保留 SDK 的流式编译和初始化 Promise 复用，避免正常环境重复下载、完整缓冲 WASM。
                await init(this.wasm);
            } catch (error) {
                // WPS 等内嵌环境可能返回非原生 Response，JS instanceof 通过也可能被原生流式接口拒绝。
                // 仅对此类型错误重取 ArrayBuffer，让 SDK 走非流式实例化；不修改全局 fetch，也不重试其他错误。
                if (
                    !(error instanceof Error) ||
                    !/Response or (?:Promise<Response>|an? Promise resolving to Response)/.test(error.message)
                ) {
                    throw error;
                }
                const response = await fetch(this.wasm);
                if (!response.ok) {
                    throw new Error(`Failed to load text WASM: HTTP ${response.status}`);
                }
                await init(await response.arrayBuffer());
            }

            this.engine = new TextEngine({
                justifyAlignStrategy: this.service.getConfig().isIntl === true ? 'default' : 'chrome-auto-like',
            });

            // 创建 Engine 会建立一套新的字体状态，因此也属于一次状态变化。
            this.fontRevision += 1;
            this.status = TextEngineServiceStatus.Ready;
        } catch (error) {
            console.error(`[TextEngineService:initialize@${engineVersion}] error`, error);
            this.engine = null;
            this.status = TextEngineServiceStatus.Error;
            throw error;
        }
    }

    public getEngine(): TextEngine | null {
        if (!this.engine || this.status !== TextEngineServiceStatus.Ready || this.engine.isDisposed) {
            return null;
        }
        return this.engine;
    }

    /**
     * 返回当前 Engine 的字体状态版本。
     *
     * 缓存 TextBuffer 或其他字体派生数据的调用方，应保存创建这些数据时的版本，并在复用前与当前值比较。
     * 调用方只应比较版本是否相同，不应从数值大小推断注册了多少字体或发生了哪种变化。
     */
    public getFontRevision(): number {
        return this.fontRevision;
    }

    /**
     * 标记同一个 Engine 实例内的字体状态已经成功变化。
     *
     * 字体模块必须在 `registerFont`、`setCommonFallback` 或 `setScriptFallback` 成功后调用；
     * 失败或幂等跳过时不能调用。调用后，先前版本对应的 TextBuffer 等派生数据应视为过期。
     * 此方法只递增版本，不负责通知、释放或更新外部消费者持有的数据。
     */
    public markFontStateChanged(): void {
        this.fontRevision += 1;
    }

    public dispose() {
        if (this.engine && !this.engine.isDisposed) {
            this.engine.dispose?.();
        }
        this.engine = null;
        this.status = TextEngineServiceStatus.Disposed;
    }
}
